import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { authAPI } from '@/api/new-api'

export const ActivityTypeEnum = z.enum([
  'reservation',
  'guest',
  'maintenance',
  'post',
  'note',
  'package',
])
export type ActivityType = z.infer<typeof ActivityTypeEnum>

export const GuestActionEnum = z.enum([
  'created',
  'updated',
  'deleted',
  'suspended',
  'unsuspended',
  'banned',
  'unbanned',
  'checkedIn',
])
export type GuestAction = z.infer<typeof GuestActionEnum>

export type GuestActivityValue = string | number | boolean | null

export type GuestActivityChange = {
  field: string
  oldValue: GuestActivityValue
  newValue: GuestActivityValue
}

export type ClientActivity = {
  id: string | number
  historyType: ActivityType
  createdAt: number
  updatedAt: number | null
  date?: number
  endDate?: number
  startTime?: number
  endTime?: number
  amenityId?: { name: string; multiDayBooking?: boolean }
  name?: string
  signature?: boolean
  status?: string
  approved?: boolean
  cancelled?: boolean
  // Note: guest entries only (visitors are guests in the backend). `action` is absent on legacy check-in rows. `name` is the
  // guest's name as stored on the log entry, so it survives deletion of the guest record.
  action?: GuestAction
  performer?: { name: string; role?: string | null }
  changes?: GuestActivityChange[]
}

type GuestPayload = Record<string, GuestActivityValue>

// Note: `details` is shaped per `historyType` by the activity-log endpoint (see
// chitowntrainer-sails `client/activitylog.js`); only the keys read below are typed.
type ActivityLogDetails = {
  action?: GuestAction
  category?: string | null
  guestName?: string | null
  performerName?: string | null
  performerRole?: string | null
  oldPayload?: GuestPayload | null
  newPayload?: GuestPayload | null
  amenityName?: string
  approved?: boolean | number
  cancelled?: boolean | number
  date?: number
  startTime?: number
  endTime?: number
  name?: string
  signature?: string | boolean | null
  status?: string
}

export type ActivityLogEntry = {
  clientId: string
  createdAt: number
  details: ActivityLogDetails
  historyType: ActivityType
  updatedAt: number | null
}

type GetActivityLogResponse = {
  total: number
  history: ActivityLogEntry[]
}

const getGuestChanges = (details: ActivityLogDetails): GuestActivityChange[] => {
  const { oldPayload, newPayload } = details
  const fields = Object.keys({ ...oldPayload, ...newPayload })
  return fields.map((field) => ({
    field,
    newValue: newPayload?.[field] ?? null,
    oldValue: oldPayload?.[field] ?? null,
  }))
}

// Note: the activity-log endpoint has no row id, so one is synthesised for list keys.
export const mapActivityLogEntry = (entry: ActivityLogEntry, index: number): ClientActivity => {
  const { details, historyType } = entry
  const base = {
    createdAt: entry.createdAt,
    historyType,
    id: `${historyType}-${entry.createdAt}-${index}`,
    updatedAt: entry.updatedAt,
  }

  switch (historyType) {
    case ActivityTypeEnum.enum.reservation:
      return {
        ...base,
        amenityId: { name: details.amenityName ?? '' },
        approved: Boolean(details.approved),
        cancelled: Boolean(details.cancelled),
        date: details.date,
        endTime: details.endTime,
        startTime: details.startTime,
      }
    case ActivityTypeEnum.enum.guest:
      return {
        ...base,
        action: details.action,
        changes: getGuestChanges(details),
        name: details.guestName ?? undefined,
        performer: {
          name: details.performerName ?? '',
          role: details.performerRole,
        },
      }
    case ActivityTypeEnum.enum.package:
      return { ...base, signature: Boolean(details.signature) }
    case ActivityTypeEnum.enum.maintenance:
      return { ...base, status: details.status }
    case ActivityTypeEnum.enum.note:
    case ActivityTypeEnum.enum.post:
      return { ...base, name: details.name }
  }
}

type GetClientActivitiesRequest = {
  clientId: string
}

export const getClientActivitiesQueryKey = (data: GetClientActivitiesRequest) => [
  'useGetClientActivities',
  data,
]

// Note: the whole log is fetched at once; filtering and paging happen client-side.
const ALL_ACTIVITIES_LIMIT = 9999

const getClientActivities = async ({ clientId }: GetClientActivitiesRequest) => {
  const response = await authAPI.get<GetActivityLogResponse>(`/client/${clientId}/activity-log`, {
    params: { limit: ALL_ACTIVITIES_LIMIT, skip: 0 },
  })
  return response.data.history.map(mapActivityLogEntry)
}

export const useGetClientActivities = (data: GetClientActivitiesRequest) =>
  useQuery({
    queryFn: () => getClientActivities(data),
    queryKey: getClientActivitiesQueryKey(data),
  })
